import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { IconBot, IconSend } from '../components/Icons';

const ChatbotView = () => {
  const { t, language } = useLanguage();
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'Hello! Feel free to ask me any questions about real estate.',
      originalText: null,
      translatedText: null,
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showTranslation, setShowTranslation] = useState({});
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputValue,
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    const apiKey = "AIzaSyDQbK0Pz7vqbFv9QrmZFZLLs-QuOxcLpeE"; // API key required for actual use

    try {
      // Generate AI response
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: `You are a helpful real estate assistant. Answer the following question in ${language === 'ko' ? 'Korean' : language === 'en' ? 'English' : language === 'zh' ? 'Chinese' : 'Japanese'}: ${inputValue}`
              }]
            }]
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        const botText = data.candidates[0].content.parts[0].text;
        
        let translatedText = null;
        let originalText = botText;

        // Translate if response is in different language
        if (language !== 'en' && !botText.match(/[\u3131-\uD79D]/)) {
          // Translate English response to user's language
          const translateResponse = await fetch(
            `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                contents: [{
                  parts: [{
                    text: `Translate the following to ${language === 'ko' ? 'Korean' : language === 'zh' ? 'Chinese' : 'Japanese'}: ${botText}`
                  }]
                }]
              })
            }
          );

          if (translateResponse.ok) {
            const translateData = await translateResponse.json();
            translatedText = translateData.candidates[0].content.parts[0].text;
          }
        }

        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          text: translatedText || botText,
          originalText: translatedText ? botText : null,
          translatedText: translatedText,
        };

        setMessages(prev => [...prev, botMessage]);
      } else {
        throw new Error('API Error');
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: t('error'),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleTranslation = (messageId) => {
    setShowTranslation(prev => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
        <IconBot className="w-8 h-8 mr-3" />
        {t('aiAssistantTitle')}
      </h1>

      <div className="bg-white rounded-lg shadow-md">
        {/* Chat messages area */}
        <div className="h-[500px] overflow-y-auto p-6 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                  className={`max-w-[70%] rounded-lg px-4 py-2 ${
                      message.type === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                  }`}
              >
                {/* 여기에 whitespace-pre-line 클래스를 추가합니다. */}
                <p className="text-sm whitespace-pre-line">
                  {showTranslation[message.id] && message.originalText // 번역 보기/원본 보기 로직도 고려
                      ? message.originalText
                      : message.text}
                </p>
                {message.originalText && (
                    <button
                        onClick={() => toggleTranslation(message.id)}
                        className="text-xs underline mt-2 block"
                    >
                      {showTranslation[message.id] ? t('viewTranslation') : t('viewOriginal')}
                    </button>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg px-4 py-2">
                <p className="text-sm text-gray-600">{t('thinking')}</p>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex space-x-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('askAnything')}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <IconSend className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotView;
